import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './GameLauncher.module.css';
import { LaunchResponse, BalanceResponse } from '../models/interface';
import { environmentConfig } from '../environments/environments';

interface SelectOption {
  value: string;
  label: string;
}

function CustomSelect({
  value,
  onChange,
  options,
}: Readonly<{ value: string; onChange: (v: string) => void; options: SelectOption[] }>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find(o => o.value === value);

  return (
    <div className={styles.customSelect} ref={ref}>
      <button type="button" className={styles.customSelectTrigger} onClick={() => setOpen(o => !o)}>
        {selected?.label ?? value}
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          className={open ? styles.customSelectChevronUp : styles.customSelectChevron}
        >
          <path
            d="M1 1l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className={styles.customSelectMenu}>
          {options.map(o => (
            <button
              key={o.value}
              type="button"
              className={`${styles.customSelectOption} ${o.value === value ? styles.customSelectOptionActive : ''}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.value === value && (
                <svg
                  width="10"
                  height="8"
                  viewBox="0 0 10 8"
                  fill="none"
                  className={styles.customSelectCheck}
                >
                  <path
                    d="M1 4l3 3 5-6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const STORAGE_KEY = 'platform_session';
const PREFS_KEY = 'platform_prefs';

interface Prefs {
  game: string;
  portalMode: string;
  platform: string;
  language: string;
  currency: string;
  userId: string;
  apiBaseUrl: string;
  iframeUrl: string;
  iframeWidth: number;
  iframeHeight: number;
}

const DEFAULT_PREFS: Prefs = {
  game: 'chicken-race',
  portalMode: 'real',
  platform: 'mobile',
  language: 'en',
  currency: 'USD',
  userId: '',
  apiBaseUrl: environmentConfig.apiBaseUrl.replace(/\/+$/, ''),
  iframeUrl: '',
  iframeWidth: 960,
  iframeHeight: 600,
};

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: Partial<Prefs>) {
  try {
    const current = loadPrefs();
    localStorage.setItem(PREFS_KEY, JSON.stringify({ ...current, ...prefs }));
  } catch {
    /* */
  }
}

interface SavedSession {
  gameData: LaunchResponse;
  params: {
    portalMode: string;
    platform: string;
    language: string;
    game: string;
    currency: string;
    userId: string;
  };
}

function saveSession(gameData: LaunchResponse, params: SavedSession['params']) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ gameData, params }));
  } catch {
    /* */
  }
}

function loadSession(): SavedSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearSession() {
  sessionStorage.removeItem(STORAGE_KEY);
}

const LANGUAGES = [
  { value: 'en', label: 'EN' },
  { value: 'ka', label: 'KA' },
  { value: 'es', label: 'ES' },
  { value: 'fr', label: 'FR' },
  { value: 'it', label: 'IT' },
  { value: 'de', label: 'DE' },
  { value: 'br', label: 'BR' },
  { value: 'fi', label: 'FI' },
  { value: 'no', label: 'NO' },
  { value: 'ru', label: 'RU' },
];

const PRESETS = [
  { label: 'Mobile S', w: 320, h: 568 },
  { label: 'Mobile', w: 414, h: 736 },
  { label: 'Tablet', w: 768, h: 1024 },
  { label: 'Desktop', w: 960, h: 600 },
  { label: 'Wide', w: 1280, h: 720 },
  { label: 'Full', w: 0, h: 600 },
];

interface ParsedCurlRequest {
  method: string;
  url: string;
  headers: Record<string, string>;
  body: string | null;
}

interface CurlExecutionResult {
  status: number;
  statusText: string;
  durationMs: number;
  finalUrl: string;
  headers: Array<[string, string]>;
  body: string;
}

const CURL_OPTIONS_WITH_VALUE = new Set([
  '-X',
  '--request',
  '-H',
  '--header',
  '-d',
  '--data',
  '--data-raw',
  '--data-binary',
  '--data-urlencode',
  '-u',
  '--user',
  '--url',
  '-A',
  '--user-agent',
  '-e',
  '--referer',
  '-b',
  '--cookie',
  '--cookie-jar',
  '--proxy',
  '--connect-timeout',
  '--max-time',
  '-m',
  '--retry',
  '-o',
  '--output',
]);

function tokenizeCurlCommand(command: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let quote: '"' | "'" | null = null;
  let escaped = false;
  const normalized = command.replace(/\\\r?\n/g, ' ').trim();

  for (const ch of normalized) {
    if (escaped) {
      current += ch;
      escaped = false;
      continue;
    }

    if (ch === '\\' && quote !== "'") {
      escaped = true;
      continue;
    }

    if (quote === "'") {
      if (ch === "'") quote = null;
      else current += ch;
      continue;
    }

    if (quote === '"') {
      if (ch === '"') quote = null;
      else current += ch;
      continue;
    }

    if (ch === "'" || ch === '"') {
      quote = ch;
      continue;
    }

    if (/\s/.test(ch)) {
      if (current) {
        tokens.push(current);
        current = '';
      }
      continue;
    }

    current += ch;
  }

  if (escaped) current += '\\';
  if (current) tokens.push(current);
  return tokens;
}

function parseCurlCommand(command: string): ParsedCurlRequest {
  const tokens = tokenizeCurlCommand(command);
  if (!tokens.length) throw new Error('Paste a cURL command first');

  let idx = tokens[0] === 'curl' ? 1 : 0;
  let method = '';
  let url = '';
  const headers: Record<string, string> = {};
  const bodyParts: string[] = [];

  while (idx < tokens.length) {
    const token = tokens[idx];

    if (token === '-X' || token === '--request') {
      const value = tokens[idx + 1];
      if (!value) throw new Error(`Missing value for ${token}`);
      method = value.toUpperCase();
      idx += 2;
      continue;
    }

    if (token === '-H' || token === '--header') {
      const value = tokens[idx + 1];
      if (!value) throw new Error(`Missing value for ${token}`);
      const sep = value.indexOf(':');
      if (sep <= 0) throw new Error(`Invalid header: ${value}`);
      const name = value.slice(0, sep).trim();
      const headerValue = value.slice(sep + 1).trim();
      headers[name] = headerValue;
      idx += 2;
      continue;
    }

    if (
      token === '-d' ||
      token === '--data' ||
      token === '--data-raw' ||
      token === '--data-binary' ||
      token === '--data-urlencode'
    ) {
      const value = tokens[idx + 1];
      if (value == null) throw new Error(`Missing value for ${token}`);
      bodyParts.push(value);
      if (!method) method = 'POST';
      idx += 2;
      continue;
    }

    if (token === '-u' || token === '--user') {
      const value = tokens[idx + 1];
      if (!value) throw new Error(`Missing value for ${token}`);
      headers.Authorization = `Basic ${btoa(value)}`;
      idx += 2;
      continue;
    }

    if (token === '--url') {
      const value = tokens[idx + 1];
      if (!value) throw new Error('Missing value for --url');
      url = value;
      idx += 2;
      continue;
    }

    if (token === '-I' || token === '--head') {
      method = 'HEAD';
      idx += 1;
      continue;
    }

    if (
      token === '-s' ||
      token === '--silent' ||
      token === '-L' ||
      token === '--location' ||
      token === '--compressed' ||
      token === '-k' ||
      token === '--insecure'
    ) {
      idx += 1;
      continue;
    }

    if (CURL_OPTIONS_WITH_VALUE.has(token)) {
      idx += 2;
      continue;
    }

    if (/^https?:\/\//i.test(token)) {
      url = token;
      idx += 1;
      continue;
    }

    if (token.startsWith('-')) {
      idx += 1;
      continue;
    }

    idx += 1;
  }

  if (!url) throw new Error('No URL detected in cURL command');

  if (!method) method = bodyParts.length ? 'POST' : 'GET';
  const body = bodyParts.length ? bodyParts.join('&') : null;

  return {
    method,
    url,
    headers,
    body,
  };
}

function GameLauncher() {
  const prefs = loadPrefs();
  const [game, setGame] = useState(prefs.game);
  const [portalMode, setPortalMode] = useState(prefs.portalMode);
  const [platform, setPlatform] = useState(prefs.platform);
  const [language, setLanguage] = useState(prefs.language);
  const [currency, setCurrency] = useState(prefs.currency);
  const [userId, setUserId] = useState(prefs.userId);
  const [apiBaseUrl, setApiBaseUrl] = useState(prefs.apiBaseUrl.replace(/\/+$/, ''));
  const [apiBaseUrlInput, setApiBaseUrlInput] = useState(prefs.apiBaseUrl.replace(/\/+$/, ''));
  const [apiBaseUrlError, setApiBaseUrlError] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState(prefs.iframeUrl);
  const [iframeUrlInput, setIframeUrlInput] = useState(prefs.iframeUrl);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gameData, setGameData] = useState<LaunchResponse | null>(null);
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [curlPanelOpen, setCurlPanelOpen] = useState(true);
  const [curlCommand, setCurlCommand] = useState('');
  const [curlLoading, setCurlLoading] = useState(false);
  const [curlError, setCurlError] = useState<string | null>(null);
  const [parsedCurlRequest, setParsedCurlRequest] = useState<ParsedCurlRequest | null>(null);
  const [curlResponse, setCurlResponse] = useState<CurlExecutionResult | null>(null);

  const [iframeWidth, setIframeWidth] = useState(prefs.iframeWidth);
  const [iframeHeight, setIframeHeight] = useState(prefs.iframeHeight);
  const [iframeWidthInput, setIframeWidthInput] = useState(
    prefs.iframeWidth === 0 ? '' : String(prefs.iframeWidth)
  );
  const [iframeHeightInput, setIframeHeightInput] = useState(String(prefs.iframeHeight));

  const gameContainerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hasAutoLaunched = useRef(false);

  const defaultApiBaseUrl = environmentConfig.apiBaseUrl.replace(/\/+$/, '');

  const normalizeApiBaseUrl = useCallback((url: string) => url.trim().replace(/\/+$/, ''), []);

  const commitIframeWidth = useCallback((nextWidth: number) => {
    const normalizedWidth =
      nextWidth <= 0 ? 0 : Math.min(2560, Math.max(280, Math.round(nextWidth)));
    setIframeWidth(normalizedWidth);
    setIframeWidthInput(normalizedWidth === 0 ? '' : String(normalizedWidth));
  }, []);

  const commitIframeHeight = useCallback((nextHeight: number) => {
    const normalizedHeight = Math.min(1440, Math.max(200, Math.round(nextHeight)));
    setIframeHeight(normalizedHeight);
    setIframeHeightInput(String(normalizedHeight));
  }, []);

  const handleWidthInputBlur = () => {
    const rawWidth = iframeWidthInput.trim();
    if (!rawWidth) {
      commitIframeWidth(0);
      return;
    }

    const parsedWidth = Number(rawWidth);
    if (!Number.isFinite(parsedWidth)) {
      setIframeWidthInput(iframeWidth === 0 ? '' : String(iframeWidth));
      return;
    }

    commitIframeWidth(parsedWidth);
  };

  const handleHeightInputBlur = () => {
    const rawHeight = iframeHeightInput.trim();
    if (!rawHeight) {
      setIframeHeightInput(String(iframeHeight));
      return;
    }

    const parsedHeight = Number(rawHeight);
    if (!Number.isFinite(parsedHeight)) {
      setIframeHeightInput(String(iframeHeight));
      return;
    }

    commitIframeHeight(parsedHeight);
  };

  const handleDimensionInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.currentTarget.blur();
  };

  const applyIframeUrl = useCallback(() => {
    setIframeUrl(iframeUrlInput.trim());
  }, [iframeUrlInput]);

  const handleIframeUrlKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    applyIframeUrl();
  };

  const applyApiBaseUrl = useCallback(() => {
    const nextApiBaseUrl = normalizeApiBaseUrl(apiBaseUrlInput);
    if (!nextApiBaseUrl) {
      setApiBaseUrlError('API base URL cannot be empty');
      return;
    }

    try {
      new URL(nextApiBaseUrl);
    } catch {
      setApiBaseUrlError('Enter a valid absolute URL');
      return;
    }

    setApiBaseUrl(nextApiBaseUrl);
    setApiBaseUrlInput(nextApiBaseUrl);
    setApiBaseUrlError(null);
  }, [apiBaseUrlInput, normalizeApiBaseUrl]);

  const resetApiBaseUrl = useCallback(() => {
    setApiBaseUrl(defaultApiBaseUrl);
    setApiBaseUrlInput(defaultApiBaseUrl);
    setApiBaseUrlError(null);
  }, [defaultApiBaseUrl]);

  const handleApiBaseUrlKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    applyApiBaseUrl();
  };

  const isUsingCustomApiBaseUrl = apiBaseUrl !== defaultApiBaseUrl;

  const runCurlCommand = useCallback(async () => {
    const raw = curlCommand.trim();
    if (!raw) {
      setCurlError('Paste a cURL command first');
      return;
    }

    setCurlLoading(true);
    setCurlError(null);

    try {
      const parsed = parseCurlCommand(raw);
      setParsedCurlRequest(parsed);

      const started = performance.now();
      const response = await fetch(parsed.url, {
        method: parsed.method,
        headers: parsed.headers,
        body:
          parsed.method === 'GET' || parsed.method === 'HEAD'
            ? undefined
            : (parsed.body ?? undefined),
      });
      const durationMs = Math.round(performance.now() - started);
      const responseHeaders = Array.from(response.headers.entries());

      const rawBody = await response.text();
      let displayBody = rawBody;
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      const bodyTrimmed = rawBody.trim();

      if (
        bodyTrimmed &&
        (contentType.includes('application/json') ||
          bodyTrimmed.startsWith('{') ||
          bodyTrimmed.startsWith('['))
      ) {
        try {
          displayBody = JSON.stringify(JSON.parse(rawBody), null, 2);
        } catch {
          /* keep raw body */
        }
      }

      setCurlResponse({
        status: response.status,
        statusText: response.statusText,
        durationMs,
        finalUrl: response.url || parsed.url,
        headers: responseHeaders,
        body: displayBody || '(empty response body)',
      });
    } catch (err: unknown) {
      setCurlResponse(null);
      setParsedCurlRequest(null);
      setCurlError(err instanceof Error ? err.message : 'Failed to parse/run cURL command');
    } finally {
      setCurlLoading(false);
    }
  }, [curlCommand]);

  const clearCurlRunner = useCallback(() => {
    setCurlCommand('');
    setCurlError(null);
    setParsedCurlRequest(null);
    setCurlResponse(null);
    setCurlLoading(false);
  }, []);

  // ── Persist prefs ──
  useEffect(() => {
    savePrefs({
      game,
      portalMode,
      platform,
      language,
      currency,
      userId,
      apiBaseUrl,
      iframeUrl,
      iframeWidth,
      iframeHeight,
    });
  }, [
    game,
    portalMode,
    platform,
    language,
    currency,
    userId,
    apiBaseUrl,
    iframeUrl,
    iframeWidth,
    iframeHeight,
  ]);

  // ── Fullscreen ──
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!gameContainerRef.current) return;
    try {
      if (!document.fullscreenElement) await gameContainerRef.current.requestFullscreen();
      else await document.exitFullscreen();
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  }, []);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // ── Balance polling ──
  const fetchBalance = useCallback(
    async (uid: string) => {
      try {
        const response = await fetch(`${apiBaseUrl}/balances/${uid}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setBalance(await response.json());
        setBalanceError(null);
      } catch (err: unknown) {
        setBalanceError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setBalanceLoading(false);
      }
    },
    [apiBaseUrl]
  );

  const balanceIntervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (gameData?.userId) {
      setBalanceLoading(true);
      fetchBalance(gameData.userId);
      balanceIntervalRef.current = setInterval(() => fetchBalance(gameData.userId), 2000);
    }
    return () => {
      if (balanceIntervalRef.current) {
        clearInterval(balanceIntervalRef.current);
        balanceIntervalRef.current = null;
      }
    };
  }, [gameData?.userId, fetchBalance]);

  // ── Launch helper ──
  const doLaunch = useCallback(
    async (p: Record<string, string>) => {
      const url = `${apiBaseUrl}/game-launch?${new URLSearchParams(p).toString()}`;
      const response = await fetch(url, { method: 'GET' });
      if (!response.ok) {
        const t = await response.text();
        throw new Error(`HTTP ${response.status}: ${t || response.statusText}`);
      }
      return (await response.json()) as LaunchResponse;
    },
    [apiBaseUrl]
  );

  // ── Auto-launch ──
  useEffect(() => {
    if (hasAutoLaunched.current) return;
    hasAutoLaunched.current = true;

    const saved = loadSession();
    if (saved) {
      setGameData(saved.gameData);
      setPortalMode(saved.params.portalMode);
      setPlatform(saved.params.platform);
      setLanguage(saved.params.language);
      setGame(saved.params.game);
      setCurrency(saved.params.currency);
      setUserId(saved.params.userId);
    } else {
      setLoading(true);
      const p = {
        portalMode: 'real',
        platform: 'mobile',
        language: 'en',
        game: 'chicken-race',
        currency: 'USD',
      };
      doLaunch(p)
        .then(data => {
          setGameData(data);
          saveSession(data, { ...p, userId: '' });
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : 'Auto-launch failed');
        })
        .finally(() => setLoading(false));
    }
  }, [doLaunch]);

  const handleNewLaunch = async (e: React.FormEvent) => {
    e.preventDefault();
    setGameData(null);
    clearSession();
    setError(null);
    setLoading(true);
    try {
      const p: Record<string, string> = { portalMode, platform, language, game, currency };
      if (userId.trim()) p.user_id = userId.trim();
      const data = await doLaunch(p);
      setGameData(data);
      setBalance(null);
      setBalanceError(null);
      saveSession(data, { portalMode, platform, language, game, currency, userId });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseGame = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    setGameData(null);
    setBalance(null);
    setBalanceError(null);
    setIframeUrl('');
    setIframeUrlInput('');
    clearSession();
  };

  const reloadIframe = () => {
    if (iframeRef.current) iframeRef.current.src = iframeRef.current.src;
  };

  // ── Drag-resize ──
  const isResizing = useRef(false);
  const resizeDir = useRef<'right' | 'bottom' | 'corner'>('corner');
  const startPos = useRef({ x: 0, y: 0, w: 0, h: 0 });

  const onResizeStart = useCallback(
    (dir: 'right' | 'bottom' | 'corner') => (e: React.MouseEvent) => {
      e.preventDefault();
      isResizing.current = true;
      resizeDir.current = dir;
      startPos.current = { x: e.clientX, y: e.clientY, w: iframeWidth || 960, h: iframeHeight };

      const onMove = (ev: MouseEvent) => {
        if (!isResizing.current) return;
        const dx = ev.clientX - startPos.current.x;
        const dy = ev.clientY - startPos.current.y;
        if (dir === 'right' || dir === 'corner') commitIframeWidth(startPos.current.w + dx);
        if (dir === 'bottom' || dir === 'corner') commitIframeHeight(startPos.current.h + dy);
      };
      const onUp = () => {
        isResizing.current = false;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        if (iframeRef.current) iframeRef.current.style.pointerEvents = '';
      };
      if (iframeRef.current) iframeRef.current.style.pointerEvents = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [iframeWidth, iframeHeight, commitIframeWidth, commitIframeHeight]
  );

  const renderDimensionBar = (extraClassName = '') => (
    <div className={`${styles.dimensionBar} ${extraClassName}`.trim()}>
      <div className={styles.dimensionPresets}>
        {PRESETS.map(p => (
          <button
            key={p.label}
            type="button"
            className={`${styles.presetBtn} ${iframeWidth === p.w && iframeHeight === p.h ? styles.presetBtnActive : ''}`}
            onClick={() => {
              commitIframeWidth(p.w);
              commitIframeHeight(p.h);
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className={styles.dimensionInputs}>
        <button
          type="button"
          className={`${styles.rotateBtn} ${iframeWidth !== 0 && iframeWidth > iframeHeight ? styles.rotateBtnLandscape : ''}`}
          onClick={() => {
            if (iframeWidth === 0) return;
            const w = iframeHeight;
            const h = iframeWidth;
            commitIframeWidth(w);
            commitIframeHeight(h);
          }}
          title={iframeWidth !== 0 && iframeWidth > iframeHeight ? 'Switch to portrait' : 'Switch to landscape'}
          disabled={iframeWidth === 0}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="2" width="16" height="20" rx="2" />
          </svg>
        </button>
        <label>
          W
          <input
            type="text"
            inputMode="numeric"
            value={iframeWidthInput}
            onChange={e => setIframeWidthInput(e.target.value)}
            onBlur={handleWidthInputBlur}
            onKeyDown={handleDimensionInputKeyDown}
            placeholder="auto"
            min={280}
            max={2560}
          />
        </label>
        <span className={styles.dimensionX}>x</span>
        <label>
          H
          <input
            type="text"
            inputMode="numeric"
            value={iframeHeightInput}
            onChange={e => setIframeHeightInput(e.target.value)}
            onBlur={handleHeightInputBlur}
            onKeyDown={handleDimensionInputKeyDown}
            min={200}
            max={1440}
          />
        </label>
        <span className={styles.dimensionLabel}>
          {iframeWidth === 0 ? '100%' : iframeWidth} x {iframeHeight}
        </span>
      </div>
    </div>
  );

  const iframeSrc = iframeUrl.trim() || gameData?.launch_url || '';
  const showIframe = !!(gameData || iframeSrc);

  useEffect(() => {
    setIframeUrlInput(currentInput => (currentInput === iframeSrc ? currentInput : iframeSrc));
  }, [iframeSrc]);

  return (
    <div className={styles.layout}>
      {/* ── GAME AREA ── */}
      <div className={styles.gameArea}>
        {showIframe && !isFullscreen && renderDimensionBar(styles.dimensionBarTop)}

        {showIframe ? (
          <div className={styles.gameWindowOuter}>
            <div
              ref={gameContainerRef}
              className={`${styles.gameWindow} ${isFullscreen ? styles.gameWindowFullscreen : ''}`}
              style={
                isFullscreen
                  ? undefined
                  : {
                      width: iframeWidth === 0 ? '100%' : `${iframeWidth}px`,
                      height: `${iframeHeight}px`,
                      maxWidth: '100%',
                    }
              }
            >
              <div className={styles.gameToolbar}>
                <div className={styles.toolbarLeft}>
                  <span className={styles.gameName}>{gameData ? game : 'Preview'}</span>
                  {gameData && (
                    <span
                      className={`${styles.portalBadge} ${portalMode === 'real' ? styles.portalBadgeReal : ''}`}
                    >
                      {portalMode}
                    </span>
                  )}
                </div>
                <div className={styles.toolbarCenter}>
                  {gameData && balanceLoading && !balance && (
                    <span className={styles.toolbarBalance}>Balance: ...</span>
                  )}
                  {gameData && balanceError && (
                    <span className={styles.toolbarBalanceError}>Balance error</span>
                  )}
                  {gameData && balance && (
                    <span className={styles.toolbarBalance}>
                      Balance: <strong>{JSON.stringify(balance)}</strong>
                    </span>
                  )}
                  {!gameData && (
                    <span className={styles.toolbarBalance} style={{ opacity: 0.6 }}>
                      {iframeSrc}
                    </span>
                  )}
                </div>
                <div className={styles.toolbarRight}>
                  <button
                    type="button"
                    className={styles.toolbarBtn}
                    onClick={reloadIframe}
                    title="Reload"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="23 4 23 10 17 10" />
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className={styles.toolbarBtn}
                    onClick={toggleFullscreen}
                    title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  >
                    {isFullscreen ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="4 14 10 14 10 20" />
                        <polyline points="20 10 14 10 14 4" />
                        <line x1="14" y1="10" x2="21" y2="3" />
                        <line x1="3" y1="21" x2="10" y2="14" />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="15 3 21 3 21 9" />
                        <polyline points="9 21 3 21 3 15" />
                        <line x1="21" y1="3" x2="14" y2="10" />
                        <line x1="3" y1="21" x2="10" y2="14" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`${styles.toolbarBtn} ${styles.toolbarBtnClose}`}
                    onClick={handleCloseGame}
                    title="Close"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className={styles.iframeWrapper}>
                <iframe
                  ref={iframeRef}
                  src={iframeSrc}
                  title="Game"
                  className={styles.gameIframe}
                  allowFullScreen
                  allow="fullscreen; autoplay; clipboard-write"
                />
              </div>

              {!isFullscreen && (
                <>
                  <div className={styles.resizeRight} onMouseDown={onResizeStart('right')}>
                    <div className={styles.resizeGripV} />
                  </div>
                  <div className={styles.resizeBottom} onMouseDown={onResizeStart('bottom')}>
                    <div className={styles.resizeGripH} />
                  </div>
                  <div className={styles.resizeCorner} onMouseDown={onResizeStart('corner')}>
                    <svg
                      className={styles.resizeCornerIcon}
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                    >
                      <path
                        d="M9 1L1 9M9 5L5 9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.emptyGame}>
            <div className={styles.emptyText}>
              {loading ? 'Launching game...' : 'Enter an Iframe URL below or launch a game'}
            </div>
          </div>
        )}

        {showIframe && !isFullscreen && renderDimensionBar()}
      </div>

      {/* ── CONTROL PANEL ── */}
      <div className={styles.controlPanel}>
        <button
          type="button"
          className={styles.panelToggle}
          onClick={() => setPanelOpen(!panelOpen)}
        >
          <span>Launch Config</span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={panelOpen ? styles.chevronUp : styles.chevronDown}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        <div className={styles.formFieldWide + ' ' + styles.iframeUrlField}>
          <label>
            Iframe URL <span className={styles.optional}>(opt)</span>
          </label>
          <div className={styles.iframeUrlControls}>
            <button type="button" className={styles.iframeUrlApplyBtn} onClick={applyIframeUrl}>
              Apply
            </button>
            <input
              type="url"
              value={iframeUrlInput}
              onChange={e => setIframeUrlInput(e.target.value)}
              onKeyDown={handleIframeUrlKeyDown}
              placeholder="https://example.com"
            />
          </div>
        </div>

        {panelOpen && (
          <div className={styles.formFieldWide + ' ' + styles.apiBaseField}>
            <div className={styles.apiBaseFieldInner}>
              <div className={styles.apiBaseHeader}>
                <label>API Base URL</label>
                <span
                  className={`${styles.apiBaseBadge} ${isUsingCustomApiBaseUrl ? styles.apiBaseBadgeCustom : ''}`}
                >
                  {isUsingCustomApiBaseUrl ? 'Custom' : 'Default'}
                </span>
              </div>
              <div className={styles.apiBaseControls}>
                <button type="button" className={styles.apiBaseApplyBtn} onClick={applyApiBaseUrl}>
                  Use URL
                </button>
                <input
                  type="url"
                  value={apiBaseUrlInput}
                  onChange={e => setApiBaseUrlInput(e.target.value)}
                  onKeyDown={handleApiBaseUrlKeyDown}
                  placeholder={defaultApiBaseUrl}
                />
                <button
                  type="button"
                  className={styles.apiBaseResetBtn}
                  onClick={resetApiBaseUrl}
                  disabled={!isUsingCustomApiBaseUrl}
                >
                  Reset
                </button>
              </div>
              <div className={styles.apiBaseMeta}>
                <span>
                  Default: <code>{defaultApiBaseUrl}</code>
                </span>
                <span>
                  Active: <code>{apiBaseUrl}</code>
                </span>
              </div>
              {apiBaseUrlError && <div className={styles.apiBaseError}>{apiBaseUrlError}</div>}
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleNewLaunch} className={styles.launchForm}>
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <label>Mode</label>
                    <CustomSelect
                      value={portalMode}
                      onChange={setPortalMode}
                      options={[
                        { value: 'real', label: 'Real' },
                        { value: 'demo', label: 'Demo' },
                      ]}
                    />
                  </div>
                  <div className={styles.formField}>
                    <label>Platform</label>
                    <CustomSelect
                      value={platform}
                      onChange={setPlatform}
                      options={[
                        { value: 'mobile', label: 'Mobile' },
                        { value: 'desktop', label: 'Desktop' },
                      ]}
                    />
                  </div>
                  <div className={styles.formField}>
                    <label>Lang</label>
                    <CustomSelect value={language} onChange={setLanguage} options={LANGUAGES} />
                  </div>
                  <div className={styles.formField}>
                    <label>Currency</label>
                    <CustomSelect
                      value={currency}
                      onChange={setCurrency}
                      options={[
                        { value: 'USD', label: 'USD' },
                        { value: 'EUR', label: 'EUR' },
                        { value: 'GEL', label: 'GEL' },
                        { value: 'TRY', label: 'TRY' },
                      ]}
                    />
                  </div>
                  <div className={styles.formFieldWide}>
                    <label>Game</label>
                    <input
                      type="text"
                      value={game}
                      onChange={e => setGame(e.target.value)}
                      required
                    />
                  </div>
                  <div className={styles.formFieldWide}>
                    <label>
                      User ID <span className={styles.optional}>(opt)</span>
                    </label>
                    <input
                      type="text"
                      value={userId}
                      onChange={e => setUserId(e.target.value)}
                      placeholder="auto"
                    />
                  </div>
                  <div className={styles.formFieldBtn}>
                    <button type="submit" className={styles.launchBtn} disabled={loading}>
                      {loading ? '...' : gameData ? 'Relaunch' : 'Launch'}
                    </button>
                  </div>
                </div>
              </form>

              {error && (
                <div className={styles.errorBox}>
                  <strong>Error:</strong> {error}
                </div>
              )}

              {gameData && (
                <details className={styles.detailsBlock} open>
                  <summary>Session Info</summary>
                  <div className={styles.sessionGrid}>
                    {(
                      [
                        ['Session', gameData.session_token, 'session_token'],
                        ['User ID', gameData.userId, 'userId'],
                        ['URL', gameData.launch_url, 'launch_url'],
                        ['API Base URL', apiBaseUrl, 'api_base_url'],
                        ['Iframe URL', iframeSrc, 'iframe_url'],
                      ] as const
                    ).map(([label, val, key]) => (
                      <div key={key} className={styles.sessionRow}>
                        <span className={styles.sessionLabel}>{label}</span>
                        <code className={styles.sessionValue}>{val}</code>
                        <button
                          type="button"
                          className={styles.copyBtn}
                          onClick={() => copyToClipboard(val, key)}
                        >
                          {copiedField === key ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    ))}
                  </div>
                  <a
                    href={iframeSrc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.openTabLink}
                  >
                    Open in New Tab
                  </a>
                </details>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── cURL Runner ── */}
      <div className={styles.curlPanel}>
        <button
          type="button"
          className={styles.panelToggle}
          onClick={() => setCurlPanelOpen(!curlPanelOpen)}
        >
          <span>cURL Request Runner</span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={curlPanelOpen ? styles.chevronUp : styles.chevronDown}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {curlPanelOpen && (
          <div className={styles.curlPanelBody}>
            <label className={styles.curlLabel}>Paste cURL command</label>
            <textarea
              className={styles.curlTextarea}
              value={curlCommand}
              onChange={e => setCurlCommand(e.target.value)}
              placeholder={`curl --request GET "${apiBaseUrl}/balances/123"`}
            />

            <div className={styles.curlActionRow}>
              <button
                type="button"
                className={styles.curlRunBtn}
                onClick={runCurlCommand}
                disabled={curlLoading}
              >
                {curlLoading ? 'Running...' : 'Run Request'}
              </button>
              <button
                type="button"
                className={styles.curlClearBtn}
                onClick={clearCurlRunner}
                disabled={curlLoading}
              >
                Clear
              </button>
            </div>

            {curlError && (
              <div className={styles.errorBox}>
                <strong>Error:</strong> {curlError}
              </div>
            )}

            {parsedCurlRequest && (
              <details className={styles.detailsBlock} open>
                <summary>Parsed Request</summary>
                <div className={styles.curlInfoGrid}>
                  <div className={styles.curlInfoRow}>
                    <span className={styles.curlInfoKey}>Method</span>
                    <code className={styles.curlInfoValue}>{parsedCurlRequest.method}</code>
                  </div>
                  <div className={styles.curlInfoRow}>
                    <span className={styles.curlInfoKey}>URL</span>
                    <code className={styles.curlInfoValue}>{parsedCurlRequest.url}</code>
                  </div>
                  <div className={styles.curlInfoRow}>
                    <span className={styles.curlInfoKey}>Headers</span>
                    <code className={styles.curlInfoValue}>
                      {Object.keys(parsedCurlRequest.headers).length
                        ? JSON.stringify(parsedCurlRequest.headers, null, 2)
                        : '(none)'}
                    </code>
                  </div>
                  <div className={styles.curlInfoRow}>
                    <span className={styles.curlInfoKey}>Body</span>
                    <code className={styles.curlInfoValue}>
                      {parsedCurlRequest.body || '(none)'}
                    </code>
                  </div>
                </div>
              </details>
            )}

            {curlResponse && (
              <div className={styles.curlResponseBlock}>
                <div className={styles.curlResponseMeta}>
                  <span
                    className={`${styles.curlStatusBadge} ${curlResponse.status >= 200 && curlResponse.status < 300 ? styles.curlStatusOk : styles.curlStatusError}`}
                  >
                    {curlResponse.status} {curlResponse.statusText || ''}
                  </span>
                  <span>{curlResponse.durationMs} ms</span>
                  <button
                    type="button"
                    className={styles.copyBtn}
                    onClick={() => copyToClipboard(curlResponse.body, 'curl_response_body')}
                  >
                    {copiedField === 'curl_response_body' ? 'Copied' : 'Copy Body'}
                  </button>
                </div>
                <div className={styles.curlFinalUrl}>
                  Final URL: <code>{curlResponse.finalUrl}</code>
                </div>
                <details className={styles.detailsBlock} open>
                  <summary>Response Body</summary>
                  <pre className={styles.curlPre}>{curlResponse.body}</pre>
                </details>
                <details className={styles.detailsBlock}>
                  <summary>Response Headers</summary>
                  <pre className={styles.curlPre}>
                    {curlResponse.headers.length
                      ? curlResponse.headers.map(([k, v]) => `${k}: ${v}`).join('\n')
                      : '(none)'}
                  </pre>
                </details>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default GameLauncher;
